"use client";

import { getSlateSpacingPixels } from "@faire/retailer-shared/SlateMappings/getSlateSpacingPixels";
import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import {
  ComponentLoggingEventParams,
  SDUIComponentRenderBehaviour,
} from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import {
  BrandTileRowPlaceholder,
  ProductTileRowPlaceholder,
} from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridRowPlaceholder";
import { SDUIGridHeaderRow } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridHeaderRow";
import { SDUIGridRow } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/SDUIGridRow";
import { FilterablePaginatedSectionContext } from "@faire/retailer-visitor-shared/components/SDUI/SectionRenderers/FilterablePaginatedSection/__internal__/FilterablePaginatedSectionContext";
import {
  DynamicVariable,
  assignInlineVars,
  mapResponsivePropToCSSVars,
} from "@faire/retailer-visitor-shared/lib/tailwindUtils";
import { Column } from "@faire/web--source/slate/Layout";
import { cn } from "@faire/web--source/tools/cn";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import { IGridStyle } from "@faire/web-api--source/indigofair/data/IGridStyle";
import { ITile } from "@faire/web-api--source/indigofair/data/ITile";
import { Spacing } from "@faire/web-api--source/indigofair/data/Spacing";
import chunk from "lodash/chunk";
import React, { type JSX, PropsWithChildren, useContext, useMemo } from "react";

const FALLBACK_NUMBER_OF_TILES_PER_ROW = 2;

const varMobileNumColumns: DynamicVariable<"--f-sdui-grid-nc-m"> = {
  variable: "--f-sdui-grid-nc-m",
  className: "grid-cols-(--f-sdui-grid-nc-m)",
};

const varTabletNumColumns: DynamicVariable<"--f-sdui-grid-nc-md"> = {
  variable: "--f-sdui-grid-nc-md",
  className: "md:grid-cols-(--f-sdui-grid-nc-md)",
};

const varDesktopNumColumns: DynamicVariable<"--f-sdui-grid-nc-lg"> = {
  variable: "--f-sdui-grid-nc-lg",
  className: "lg:grid-cols-(--f-sdui-grid-nc-lg)",
};

const varXLargeNumColumns: DynamicVariable<"--f-sdui-grid-nc-xl"> = {
  variable: "--f-sdui-grid-nc-xl",
  className: "xl:grid-cols-(--f-sdui-grid-nc-xl)",
};

export const SDUIGridFixedColumnLayoutBase: React.FC<{
  gridComponent: IGridComponent;
  loggingData: ComponentLoggingEventParams;
  isRequiredForInteractivity?: boolean;
  renderBehaviour?: SDUIComponentRenderBehaviour;
}> = ({
  gridComponent,
  isRequiredForInteractivity,
  loggingData,
  renderBehaviour,
}) => {
  const {
    column_layout = IGridComponent.IColumnLayout.build(),
    header_background_icon,
    log_tile_batch_view,
    responsive_header_background_icon,
    responsive_title,
    style = IGridStyle.build(),
    tiles,
    title,
  } = gridComponent;

  const {
    column_spacing = Spacing.SPACING_NONE,
    row_spacing = Spacing.SPACING_NONE,
  } = style;

  const responsiveNumberOfTilesPerRow =
    convertServerResponsivePropertyToClientResponsiveProperty(
      column_layout.fixed?.num_columns ?? FALLBACK_NUMBER_OF_TILES_PER_ROW,
      (col) => col,
      column_layout.fixed?.responsive_num_columns
    );

  const responsiveNumberOfTilesPerRowForCSSVars =
    convertServerResponsivePropertyToClientResponsiveProperty(
      column_layout.fixed?.num_columns ?? FALLBACK_NUMBER_OF_TILES_PER_ROW,
      (col) => `repeat(${col}, 1fr)`,
      column_layout.fixed?.responsive_num_columns
    );

  const currentNumberOfTilesPerRow = useResponsiveValue(
    responsiveNumberOfTilesPerRow
  );

  const { vars, classes } = mapResponsivePropToCSSVars(
    responsiveNumberOfTilesPerRowForCSSVars,
    {
      mobileAndAbove: varMobileNumColumns,
      tabletAndAbove: varTabletNumColumns,
      desktopAndAbove: varDesktopNumColumns,
      xLargeAndAbove: varXLargeNumColumns,
    }
  );

  const tileChunks = useMemo(() => {
    return chunk(tiles, currentNumberOfTilesPerRow);
  }, [currentNumberOfTilesPerRow, tiles]);

  return (
    <Column className="w-full">
      <SDUIGridHeaderRow
        title={title}
        responsiveTitle={responsive_title}
        backgroundIcon={header_background_icon}
        responsiveBackgroundIcon={responsive_header_background_icon}
        loggingData={loggingData}
      />

      {tileChunks.map((tiles, index) => {
        const forceVisibility = index === 0 && isRequiredForInteractivity;
        return (
          <MaybeWhenVisibilityIsImminent
            key={tiles[0]?.logging_data?.instance_identifier ?? index}
            forceVisibility={forceVisibility}
            rowPlaceholder={getRowPlaceholder(
              tiles[0],
              `${getSlateSpacingPixels(index === 0 ? Spacing.SPACING_NONE : row_spacing)}px ${getSlateSpacingPixels(column_spacing)}px`,
              `${getSlateSpacingPixels(index === 0 ? Spacing.SPACING_NONE : row_spacing)}px`
            )}
            renderBehaviour={renderBehaviour}
          >
            <SDUIGridRow
              tiles={tiles}
              loggingData={loggingData}
              className={cn("grid", classes)}
              componentStyle={{
                ...assignInlineVars(vars),
                gap: `${getSlateSpacingPixels(index === 0 ? Spacing.SPACING_NONE : row_spacing)}px ${getSlateSpacingPixels(column_spacing)}px`,
                marginTop: `${getSlateSpacingPixels(index === 0 ? Spacing.SPACING_NONE : row_spacing)}px`,
              }}
              logTileBatchView={log_tile_batch_view}
              shouldSetIsScrollingTopToFalseOnView={
                forceVisibility && renderBehaviour?.gridOptimizations
              }
            />
          </MaybeWhenVisibilityIsImminent>
        );
      })}
    </Column>
  );
};

const getRowPlaceholder = (
  tile?: ITile,
  gap?: React.CSSProperties["gap"],
  marginTop?: React.CSSProperties["marginTop"]
) => {
  if (!tile) {
    return undefined;
  }

  if (tile.brand_tile || tile.brand_tile_with_action) {
    return <BrandTileRowPlaceholder marginTop={marginTop} />;
  } else if (tile.product_tile || tile.product_with_brand_info_tile) {
    return <ProductTileRowPlaceholder gap={gap} marginTop={marginTop} />;
  }

  return undefined;
};

const MaybeWhenVisibilityIsImminent: React.FC<
  PropsWithChildren & {
    forceVisibility?: boolean;
    rowPlaceholder?: JSX.Element;
    renderBehaviour?: SDUIComponentRenderBehaviour;
  }
> = ({ forceVisibility, rowPlaceholder, children, renderBehaviour }) => {
  const { isScrollingToTop } = useContext(FilterablePaginatedSectionContext);
  if (
    !forceVisibility &&
    isScrollingToTop &&
    renderBehaviour?.gridOptimizations
  ) {
    if (rowPlaceholder) {
      return rowPlaceholder;
    }
  }
  if (!forceVisibility && rowPlaceholder) {
    return (
      <WhenVisibilityIsImminent
        customizePlaceholder={rowPlaceholder}
        shouldRunNormallyInTest
      >
        {children}
      </WhenVisibilityIsImminent>
    );
  }

  return children;
};
